import { PlusIcon, TIcon } from '@acceso/design-system';
import { useMemo, useState } from 'react';
import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectForm } from '@/components/FormFields/SelectForm';
import { useSupportOptions } from '@/features/programs/hooks/useSupportOptions';

type TSupportSelectFormProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  additionalButtons?: {
    label: string;
    onClick: () => void;
    icon?: TIcon;
    disabled?: boolean;
  }[];
  classname?: string;
  noneOptionGender?: 'masculine' | 'feminine';
  defaultToNone?: boolean;
};

export function SupportSelectForm<T extends FieldValues>({
  form,
  name,
  label,
  required,
  disabled,
  placeholder,
  additionalButtons,
  classname,
  noneOptionGender,
  defaultToNone,
}: TSupportSelectFormProps<T>) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const { supportOptions, createSupportOption, isCreatingSupport } = useSupportOptions();

  const createButton = useMemo(() => {
    if (!searchText) return undefined;

    const alreadyExists = supportOptions.find(({ label }) => label === searchText);
    if (alreadyExists) return undefined;

    return {
      label: `${t('Añadir')} "${searchText}"`,
      icon: PlusIcon,
      disabled: disabled || isCreatingSupport,
      onClick: async () => {
        const createdSupport = await createSupportOption(searchText);
        if (!createdSupport.value) return;

        form.setValue(name, createdSupport.value as PathValue<T, Path<T>>, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setSearchText('');
      },
    };
  }, [createSupportOption, disabled, form, isCreatingSupport, name, searchText, supportOptions, t]);

  const mergedAdditionalButtons = useMemo(() => {
    return [...(additionalButtons || []), ...(createButton ? [createButton] : [])];
  }, [additionalButtons, createButton]);

  return (
    <SelectForm
      form={form}
      name={name}
      label={label}
      options={supportOptions}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      additionalButtons={mergedAdditionalButtons.length ? mergedAdditionalButtons : undefined}
      classname={classname}
      noneOptionGender={noneOptionGender}
      defaultToNone={defaultToNone}
      onFilterTextChange={setSearchText}
      withFilter={true}
    />
  );
}
